import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import type { PayloadAction } from '@reduxjs/toolkit'
import { invoicesApi, type Invoice, type InvoicesQueryParams } from '../../services/invoicesService'

// Async thunks for API calls using proper HATEOAS navigation
export const fetchInvoices = createAsyncThunk(
  'invoices/fetchInvoices',
  async (params: InvoicesQueryParams = {}, { getState }) => {
    const state = getState() as any
    const invoicesHref = state.auth.currentClinic?.invoices?.href
    
    if (!invoicesHref) {
      throw new Error('No clinic selected or clinic missing invoices link')
    }
    
    console.log('HATEOAS: Following invoices href:', invoicesHref, 'with params:', params)
    
    // This is the HATEOAS way - use the href the API gave us
    const response = await invoicesApi.getInvoices(invoicesHref, params)
    return response
  }
)

// Server-side search for invoices
export const searchInvoices = createAsyncThunk(
  'invoices/searchInvoices',
  async ({ invoicesHref, searchTerm }: { invoicesHref: string; searchTerm: string }) => {
    console.log('HATEOAS: Searching invoices with term:', searchTerm)
    
    // For now, just fetch all invoices - could be enhanced with server-side search
    const response = await invoicesApi.getInvoices(invoicesHref, {})
    return response
  }
)

// State interface
interface InvoicesState {
  invoices: Invoice[]
  currentInvoice: Invoice | null
  total: number
  loading: boolean
  error: string | null
  searchTerm: string
  isSearching: boolean
  selectedStatusFilter: string[]
  selectedDateRange: string
  // HATEOAS pagination state
  pageSize: number
  pageNumber: number
  pagePrevious: string | null
  pageNext: string | null
}

// Initial state
const initialState: InvoicesState = {
  invoices: [],
  currentInvoice: null,
  total: 0,
  loading: false,
  error: null,
  searchTerm: '',
  isSearching: false,
  selectedStatusFilter: [],
  selectedDateRange: 'all',
  pageSize: 10,
  pageNumber: 1,
  pagePrevious: null,
  pageNext: null,
}

// Slice
const invoicesSlice = createSlice({
  name: 'invoices',
  initialState,
  reducers: {
    setSearchTerm: (state, action: PayloadAction<string>) => {
      state.searchTerm = action.payload
    },
    clearError: (state) => {
      state.error = null
    },
    setSelectedStatusFilter: (state, action: PayloadAction<string[]>) => {
      state.selectedStatusFilter = action.payload
    },
    setSelectedDateRange: (state, action: PayloadAction<string>) => {
      state.selectedDateRange = action.payload
    },
    setPageSize: (state, action: PayloadAction<number>) => {
      state.pageSize = action.payload
    },
    setPageNumber: (state, action: PayloadAction<number>) => {
      state.pageNumber = action.payload
    },
    clearCurrentInvoice: (state) => {
      state.currentInvoice = null
    },
  },
  extraReducers: (builder) => {
    // Fetch invoices
    builder
      .addCase(fetchInvoices.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchInvoices.fulfilled, (state, action) => {
        state.loading = false
        state.invoices = action.payload.items
        state.total = action.payload.total_items
        state.pageSize = action.payload.page_size || state.pageSize
        state.pageNumber = action.payload.page_number || state.pageNumber
        state.pagePrevious = action.payload.page_previous
        state.pageNext = action.payload.page_next
      })
      .addCase(fetchInvoices.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || 'Failed to fetch invoices'
      })

    // Search invoices
    builder
      .addCase(searchInvoices.pending, (state) => {
        state.isSearching = true
        state.error = null
      })
      .addCase(searchInvoices.fulfilled, (state, action) => {
        state.isSearching = false
        state.invoices = action.payload.items
        state.total = action.payload.total_items
        state.pageSize = action.payload.page_size || state.pageSize
        state.pageNumber = action.payload.page_number || state.pageNumber
        state.pagePrevious = action.payload.page_previous
        state.pageNext = action.payload.page_next
      })
      .addCase(searchInvoices.rejected, (state, action) => {
        state.isSearching = false
        state.error = action.error.message || 'Failed to search invoices'
      })
  },
})

// Export actions
export const { 
  setSearchTerm, 
  clearError, 
  setSelectedStatusFilter,
  setSelectedDateRange,
  setPageSize, 
  setPageNumber,
  clearCurrentInvoice
} = invoicesSlice.actions

// Selectors
export const selectInvoices = (state: { invoices: InvoicesState }) => state.invoices.invoices
export const selectCurrentInvoice = (state: { invoices: InvoicesState }) => state.invoices.currentInvoice
export const selectInvoicesTotal = (state: { invoices: InvoicesState }) => state.invoices.total
export const selectInvoicesLoading = (state: { invoices: InvoicesState }) => state.invoices.loading
export const selectInvoicesError = (state: { invoices: InvoicesState }) => state.invoices.error
export const selectInvoicesSearching = (state: { invoices: InvoicesState }) => state.invoices.isSearching
export const selectSearchTerm = (state: { invoices: InvoicesState }) => state.invoices.searchTerm
export const selectSelectedStatusFilter = (state: { invoices: InvoicesState }) => state.invoices.selectedStatusFilter
export const selectSelectedDateRange = (state: { invoices: InvoicesState }) => state.invoices.selectedDateRange
export const selectPageSize = (state: { invoices: InvoicesState }) => state.invoices.pageSize
export const selectPageNumber = (state: { invoices: InvoicesState }) => state.invoices.pageNumber
export const selectPagePrevious = (state: { invoices: InvoicesState }) => state.invoices.pagePrevious
export const selectPageNext = (state: { invoices: InvoicesState }) => state.invoices.pageNext

export default invoicesSlice.reducer